import { useCallback, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import useSWR from 'swr';
import {
  APPROVAL_V2_URL,
  getNextConfirmApprovalUrl,
} from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import { approvalAiCarryOverAtom } from '@/components/Approval/ApprovalView/atoms';
import type { ApprovalViewResponse } from '@/components/Approval/ApprovalView/types';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';

/**
 * 결재 완료 후 "다음 결재 문서 열기" 레이어 대상 (레거시 apprAfterConfirmModal).
 * 데스크탑 모달·모바일 다이얼로그·알림 모달·조회 페이지가 같은 판정을 쓰도록 한 곳에 둠.
 * 대상 선정은 서버(next-confirm) 몫 — v1 결재 큐와 달리 신·구 화면 상신분을 함께 봄(PORTAL-13617).
 */

export type NextApprovalDoc = {
  docId: string;
  /**
   * 양식 코드 — 호스트의 조회 화면(포탈 native / 레거시 iframe) 분기용.
   * next-confirm 응답에 없어 단건 조회로 채움. undefined 면 호스트가 iframe 으로 폴백.
   */
  typeCode?: number;
};

type NextConfirmDoc = {
  documentId: string;
  subject: string;
  authoredAt: string;
} | null;

/** 오류(403/네트워크)도 "다음 문서 없음" 취급 — 레이어 없이 조용히 닫힘 */
const fetchNextConfirm = (documentId: string) =>
  getNestedDataFetcher<NextConfirmDoc>(
    getNextConfirmApprovalUrl(documentId),
  ).catch(() => null);

const fetchTypeCode = (documentId: string) =>
  getNestedDataFetcher<ApprovalViewResponse>(`${APPROVAL_V2_URL}/${documentId}`)
    .then((view) => view?.type?.code)
    .catch(() => undefined);

/**
 * 문서 id 를 호스트가 열 수 있는 형태로 — 레이어를 레거시가 그렸어도
 * 화면 선택 기준(NATIVE_VIEW_APPROVAL_TYPES)은 포탈이 그대로 씀.
 */
export const resolveApprovalDoc = async (
  documentId: string,
): Promise<NextApprovalDoc> => ({
  docId: documentId,
  typeCode: await fetchTypeCode(documentId),
});

/**
 * @param documentId 지금 보고 있는 문서. 이 문서 기준으로 다음 대상 프리페치.
 * @param shouldFetch 조회 화면이 열려 있는 동안만 필요한 호스트(모바일 다이얼로그·알림)용 게이트.
 */
const useNextApprovalDoc = (documentId?: string | null, shouldFetch = true) => {
  // 결재 후 호출은 레이어가 늦게 뜸. 서버가 현재 문서를 빼고 고르므로 결재 전후 응답이 같아 프리페치 가능
  const { data: prefetched } = useSWR<NextConfirmDoc>(
    shouldFetch && documentId ? getNextConfirmApprovalUrl(documentId) : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false },
  );

  // null = 다음 문서 없음(레이어 미노출)
  const [nextDoc, setNextDoc] = useState<NextApprovalDoc | null>(null);

  /**
   * 결재 흐름 세대. promptNext 는 최대 2회 왕복(next-confirm + 단건조회)하는 동안 호스트가
   * 모달을 닫거나 다른 문서를 열 수 있어, 도착한 응답이 아직 유효한지 이걸로 판별함.
   */
  const flowSeqRef = useRef(0);

  /**
   * 보류(H)는 문서가 내 큐에 남아 본인이 재처리 — 서버는 현재 문서만 빼므로 여기서 걸러야 함.
   * `stale` = 응답 도착 전에 흐름이 바뀜. 호스트는 레이어도 띄우지 말고 닫지도 말 것
   * (이미 닫혔거나 다른 문서를 보고 있으므로 닫으면 그 문서가 사라짐).
   */
  const setAiCarryOver = useSetAtom(approvalAiCarryOverAtom);

  const promptNext = useCallback(
    async (decidedDocId: string, action: ApprovalDecisionAction) => {
      if (action === 'hold') return { next: null, stale: false };

      const seq = ++flowSeqRef.current;

      // 프리페치는 보고 있던 문서 기준일 때만 유효. 아직 안 왔으면(undefined) 그 자리에서 조회
      const candidate =
        documentId === decidedDocId && prefetched !== undefined
          ? prefetched
          : await fetchNextConfirm(decidedDocId);

      const picked: NextApprovalDoc | null = candidate
        ? {
            docId: candidate.documentId,
            typeCode: await fetchTypeCode(candidate.documentId),
          }
        : null;

      if (seq !== flowSeqRef.current) return { next: null, stale: true };

      // 다음 문서가 없으면 AI 패널을 이어 열 곳도 없다 — 나중에 연 다른 문서에서 갑자기 열리지 않게 (PORTAL-15735)
      if (!picked) setAiCarryOver(null);
      setNextDoc(picked);
      return { next: picked, stale: false };
    },
    [documentId, prefetched, setAiCarryOver],
  );

  /**
   * 레이어를 내리는 모든 경로(닫기·새 문서 오픈)에서 호출 — 진행 중이던 응답도 같이 무효화.
   * AI 패널 이어 열기 표시도 지운다: 같은 화면에서 다음 문서로 바뀌면 패널 상태가 그대로 유지되므로 필요 없고,
   * 닫거나 다른 문서를 새로 열 때는 남아 있으면 안 된다. 새 웹뷰로 다음 문서를 띄우는 앱 경로만 keepAiCarryOver (PORTAL-15735).
   */
  const clearNextDoc = useCallback(
    (options?: { keepAiCarryOver?: boolean }) => {
      flowSeqRef.current += 1;
      setNextDoc(null);
      if (!options?.keepAiCarryOver) setAiCarryOver(null);
    },
    [setAiCarryOver],
  );

  return { nextDoc, promptNext, clearNextDoc };
};

export default useNextApprovalDoc;
